import { useState } from 'react'
import { AlertTriangle, UserX } from 'lucide-react'
import { useSyncEliminations, type SyncEliminationsResult } from '../queries'
import styles from './SeedRostersForm.module.css'

type SyncEliminationsFormProps = {
  competitionId: string
}

export function SyncEliminationsForm({ competitionId }: SyncEliminationsFormProps) {
  const [lastResult, setLastResult] = useState<SyncEliminationsResult | null>(null)
  const sync = useSyncEliminations()

  function handleSync() {
    sync.mutate(competitionId, { onSuccess: (result) => setLastResult(result) })
  }

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>5 · Sincronizar Eliminações</h3>
      <p className={styles.hint}>
        Marca as seleções eliminadas a partir do chaveamento já importado: um time está fora
        quando a próxima fase tem confrontos definidos e ele não aparece em nenhum. Cobre a fase
        de grupos (ausente do mata-mata 32) e cada rodada eliminatória. Não chama a API — rode
        depois de importar cada nova fase (passo 2). <strong>Idempotente.</strong>
      </p>

      <button
        type="button"
        className={styles.btn}
        onClick={handleSync}
        disabled={sync.isPending}
      >
        {sync.isPending ? <span className={styles.spinner} aria-hidden="true" /> : <UserX size={14} />}
        {sync.isPending ? 'Sincronizando…' : 'Sincronizar Eliminações'}
      </button>

      {lastResult && (
        <div className={styles.result}>
          <div className={styles.resultTitle}>Último resultado</div>
          <div className={styles.actionStats}>
            <Stat label="Seleções eliminadas" value={lastResult.teamsEliminated} accent />
            <Stat label="Transferências creditadas" value={lastResult.creditsGranted} accent />
          </div>

          {lastResult.eliminated.length > 0 && (
            <ul className={styles.warningsList}>
              {lastResult.eliminated.map((t) => (
                <li key={t.code}>
                  {t.code} — {t.name} (após a rodada {t.afterGameweek})
                </li>
              ))}
            </ul>
          )}

          {lastResult.warnings.length > 0 && (
            <div className={styles.warnings}>
              <div className={styles.warningsHeader}>
                <AlertTriangle size={12} />
                {lastResult.warnings.length} aviso{lastResult.warnings.length === 1 ? '' : 's'}
              </div>
              <ul className={styles.warningsList}>
                {lastResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statLabel}>{label}</span>
      <span className={accent ? styles.statValueAccent : styles.statValue}>{value}</span>
    </div>
  )
}
